import {
  type CrowdexUIMessage,
  crowdexCommandsDataSchema,
  crowdexDiffDataSchema,
  crowdexElicitationDataSchema,
  crowdexMcpAppDataSchema,
  crowdexMessageMetadataSchema,
  crowdexPlanDataSchema,
  crowdexSessionEventDataSchema,
  crowdexSteerDataSchema,
  crowdexSubagentDataSchema,
  crowdexTerminalDataSchema,
  crowdexUsageDataSchema,
} from '@crowdex/agent-contracts';
import { z } from 'zod';

/**
 * `UIMessage` part union, narrowed to what Crowdex actually persists. Derived
 * from the column type so the persisted schema stays aligned with
 * `@crowdex/agent-contracts` through `@crowdex/db`.
 */
export type MessagePart = CrowdexUIMessage['parts'][number];
export type MessageMetadata = NonNullable<CrowdexUIMessage['metadata']>;
export type DynamicToolPart = Extract<MessagePart, { type: 'dynamic-tool' }>;

const GENERIC_TOOL_FAILURES = new Set(['Tool call failed.', 'The tool call failed.']);
const dataPartIdentitySchema = { id: z.string().min(1).max(256).optional() } as const;

export function visibleToolErrorText(
  errorText: string | undefined,
  hasTerminal: boolean,
): string | undefined {
  return hasTerminal && errorText !== undefined && GENERIC_TOOL_FAILURES.has(errorText)
    ? undefined
    : errorText;
}

/**
 * Wire shape accepted from the desktop app. Deliberately narrower than the AI
 * SDK union: optional presentation-only fields are dropped so that the mapper
 * below can rebuild exact object literals under `exactOptionalPropertyTypes`.
 */
const approvalSchema = z.object({
  id: z.string(),
  approved: z.boolean().optional(),
  reason: z.string().optional(),
  isAutomatic: z.boolean().optional(),
  signature: z.string().optional(),
});
const jsonObjectSchema = z.record(z.string(), z.json());
const providerMetadataSchema = z.record(z.string(), jsonObjectSchema);

const toolPartSchema = z.object({
  type: z.literal('dynamic-tool'),
  toolName: z.string(),
  toolCallId: z.string(),
  title: z.string().optional(),
  toolMetadata: jsonObjectSchema.optional(),
  providerExecuted: z.boolean().optional(),
  state: z.enum([
    'input-streaming',
    'input-available',
    'approval-requested',
    'approval-responded',
    'output-available',
    'output-error',
    'output-denied',
  ]),
  input: z.json().optional(),
  rawInput: z.json().optional(),
  output: z.json().optional(),
  errorText: z.string().optional(),
  callProviderMetadata: providerMetadataSchema.optional(),
  resultProviderMetadata: providerMetadataSchema.optional(),
  preliminary: z.boolean().optional(),
  approval: approvalSchema.optional(),
});

const canonicalWirePartSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string() }),
  z.object({ type: z.literal('reasoning'), text: z.string() }),
  z.object({ type: z.literal('step-start') }),
  z.object({ type: z.literal('file'), mediaType: z.string(), url: z.string() }),
  z.object({
    type: z.literal('source-url'),
    sourceId: z.string(),
    url: z.string(),
  }),
  z.object({
    type: z.literal('source-document'),
    sourceId: z.string(),
    mediaType: z.string(),
    title: z.string(),
    filename: z.string().optional(),
  }),
  toolPartSchema,
  z.object({
    type: z.literal('data-plan'),
    data: crowdexPlanDataSchema,
    ...dataPartIdentitySchema,
  }),
  z.object({
    type: z.literal('data-diff'),
    data: crowdexDiffDataSchema,
    ...dataPartIdentitySchema,
  }),
  z.object({
    type: z.literal('data-terminal'),
    data: crowdexTerminalDataSchema,
    ...dataPartIdentitySchema,
  }),
  z.object({
    type: z.literal('data-usage'),
    data: crowdexUsageDataSchema,
    ...dataPartIdentitySchema,
  }),
  z.object({
    type: z.literal('data-commands'),
    data: crowdexCommandsDataSchema,
    ...dataPartIdentitySchema,
  }),
  z.object({
    type: z.literal('data-subagent'),
    data: crowdexSubagentDataSchema,
    ...dataPartIdentitySchema,
  }),
  z.object({
    type: z.literal('data-mcpApp'),
    data: crowdexMcpAppDataSchema,
    ...dataPartIdentitySchema,
  }),
  z.object({
    type: z.literal('data-sessionEvent'),
    data: crowdexSessionEventDataSchema,
    ...dataPartIdentitySchema,
  }),
  z.object({
    type: z.literal('data-elicitation'),
    data: crowdexElicitationDataSchema,
    ...dataPartIdentitySchema,
  }),
  z.object({
    type: z.literal('data-steer'),
    data: crowdexSteerDataSchema,
    ...dataPartIdentitySchema,
  }),
]);

/** AI SDK emits declared host tools as `tool-<name>` rather than `dynamic-tool`. */
const wirePartSchema = z.preprocess((value) => {
  if (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    typeof value.type === 'string' &&
    value.type.startsWith('tool-')
  ) {
    return { ...value, type: 'dynamic-tool', toolName: value.type.slice('tool-'.length) };
  }
  return value;
}, canonicalWirePartSchema);

type WirePart = z.infer<typeof wirePartSchema>;
type WireToolPart = Extract<WirePart, { type: 'dynamic-tool' }>;

function toToolPart(part: WireToolPart): MessagePart {
  const head = {
    type: 'dynamic-tool',
    toolName: part.toolName,
    toolCallId: part.toolCallId,
    ...(part.title === undefined ? {} : { title: part.title }),
    ...(part.toolMetadata === undefined ? {} : { toolMetadata: part.toolMetadata }),
    ...(part.providerExecuted === undefined ? {} : { providerExecuted: part.providerExecuted }),
  } as const;
  const input = part.input ?? null;
  const providerFields = {
    ...(part.callProviderMetadata === undefined
      ? {}
      : { callProviderMetadata: part.callProviderMetadata }),
    ...(part.resultProviderMetadata === undefined
      ? {}
      : { resultProviderMetadata: part.resultProviderMetadata }),
  };
  const approval = part.approval;

  switch (part.state) {
    case 'input-streaming':
      return { ...head, ...providerFields, state: 'input-streaming', input };
    case 'input-available':
      return { ...head, ...providerFields, state: 'input-available', input };
    case 'approval-requested':
      return {
        ...head,
        ...providerFields,
        state: 'approval-requested',
        input,
        approval: {
          id: approval?.id ?? part.toolCallId,
          ...(approval?.isAutomatic === undefined ? {} : { isAutomatic: approval.isAutomatic }),
          ...(approval?.signature === undefined ? {} : { signature: approval.signature }),
        },
      };
    case 'approval-responded':
      return {
        ...head,
        ...providerFields,
        state: 'approval-responded',
        input,
        approval: {
          ...(approval ?? { id: part.toolCallId }),
          approved: approval?.approved ?? false,
        },
      };
    case 'output-available':
      return {
        ...head,
        ...providerFields,
        state: 'output-available',
        input,
        output: part.output ?? null,
        ...(part.preliminary === undefined ? {} : { preliminary: part.preliminary }),
        ...(approval === undefined ? {} : { approval: { ...approval, approved: true as const } }),
      };
    case 'output-error':
      return {
        ...head,
        ...providerFields,
        state: 'output-error',
        input,
        ...(part.rawInput === undefined ? {} : { rawInput: part.rawInput }),
        errorText: part.errorText ?? 'The tool call failed.',
        ...(approval === undefined ? {} : { approval: { ...approval, approved: true as const } }),
      };
    case 'output-denied':
      return {
        ...head,
        ...providerFields,
        state: 'output-denied',
        input,
        approval: {
          ...(approval ?? { id: part.toolCallId }),
          approved: false,
        },
      };
  }
}

function toMessagePart(part: WirePart): MessagePart {
  const identity = 'id' in part && part.id !== undefined ? { id: part.id } : {};
  switch (part.type) {
    case 'text':
      return { type: 'text', text: part.text };
    case 'reasoning':
      return { type: 'reasoning', text: part.text };
    case 'step-start':
      return { type: 'step-start' };
    case 'file':
      return { type: 'file', mediaType: part.mediaType, url: part.url };
    case 'source-url':
      return { type: 'source-url', sourceId: part.sourceId, url: part.url };
    case 'source-document':
      return {
        type: 'source-document',
        sourceId: part.sourceId,
        mediaType: part.mediaType,
        title: part.title,
        ...(part.filename === undefined ? {} : { filename: part.filename }),
      };
    case 'dynamic-tool':
      return toToolPart(part);
    case 'data-plan':
      return { type: 'data-plan', data: part.data, ...identity };
    case 'data-diff':
      return { type: 'data-diff', data: part.data, ...identity };
    case 'data-terminal':
      return { type: 'data-terminal', data: part.data, ...identity };
    case 'data-usage':
      return { type: 'data-usage', data: part.data, ...identity };
    case 'data-commands':
      return { type: 'data-commands', data: part.data, ...identity };
    case 'data-subagent':
      return { type: 'data-subagent', data: part.data, ...identity };
    case 'data-mcpApp':
      return { type: 'data-mcpApp', data: part.data, ...identity };
    case 'data-sessionEvent':
      return { type: 'data-sessionEvent', data: part.data, ...identity };
    case 'data-elicitation':
      return { type: 'data-elicitation', data: part.data, ...identity };
    case 'data-steer':
      return { type: 'data-steer', data: part.data, ...identity };
  }
}

export const messagePartsSchema = z
  .array(wirePartSchema)
  .max(512)
  .transform((parts): MessagePart[] => parts.map(toMessagePart));

export const messageMetadataSchema = crowdexMessageMetadataSchema.transform(
  (value): MessageMetadata => {
    // Metadata owned by plugins is namespaced and deliberately opaque to the
    // host. Preserve it while the shared schema validates the core fields.
    return { ...value } as MessageMetadata;
  },
);
